import { ChangeDetectorRef, Component } from '@angular/core';
import { FieldWrapper } from '@ngx-formly/core';
import { SlInput } from '@shoelace-style/shoelace';
import { AdminPriceOverrideRequest, SaveResponse, SectionType, SummaryFormSections, SummarySection, SummarySectionElements } from 'src/app/interfaces/form-model';
import { CurrencyService } from 'src/app/service/currency.service';
import { FormDiscountCodeService } from 'src/app/service/form-discount-code.service';
import { FormSaveService } from 'src/app/service/form-save.service';
import { LoadingService } from 'src/app/service/loading.service';

@Component({
  selector: 'app-summary-section',
  templateUrl: './summary-section.component.html',
  styleUrls: ['./summary-section.component.scss']
})
export class SummarySectionComponent extends FieldWrapper {

  editing = false;
  adminPriceOverrideRequest!: AdminPriceOverrideRequest;

  discountCodeResponse: SaveResponse = {
    errors: [],
    refreshPage: false,
    response: {}
  };

  constructor(
    private currencyService: CurrencyService,
    private formSaveService: FormSaveService,
    private formDiscountCodeService: FormDiscountCodeService,
    private changeDetectorRef: ChangeDetectorRef,
    private loadingService: LoadingService
  ) {
    super();
  }

  summarySection!: SummarySection;

  ngOnInit() {
    this.summarySection = this.props as unknown as SummarySection;
  }

  getTotal() {
    return this.currencyService.formatCurrency(this.props['totalDiscountedAmount']);
  }

  findKeyInJson(obj: any, keyToFind: string): boolean {

    if (typeof obj !== 'object' || obj === null) {
      return false; // Not an object or array
    }

    // Check if the key exists and its value is `true`
    if (keyToFind in obj && obj[keyToFind] === true) {
      return true; // Found the key with value `true`
    }

    // Recursively search in nested objects or arrays
    for (const key in obj) {
      if (this.findKeyInJson(obj[key], keyToFind)) {
        return true;
      }
    }

    return false; // Key not found or value is not `true`
  }

  getSectionObject(section: SummaryFormSections) {

    section.adminOverride = this.editing;
    section.callback = this.updatePriceChangeRequest;

    return section;
  }

  updatePriceChangeRequest = (value: number, selection: number, element: SummarySectionElements) => {

    // Initialize the AdminPriceOverrideRequest object if it doesn't exist
    if (!this.adminPriceOverrideRequest)
      this.adminPriceOverrideRequest = this.getPriceChanges(this.summarySection, this.adminPriceOverrideRequest);

    const adminPricing = this.adminPriceOverrideRequest.adminPricing;

    // Determine the section type and update the corresponding array
    switch (element.sectionType) {
      case SectionType.APPLICANT: {
        this.updateField(
          adminPricing.adminPricedApplicantFields,
          {
            fieldId: element.id,
            price: value,
            selectionId: selection,
            applicantId: element.applicantId,
          },
          ['fieldId', 'selectionId', 'applicantId']
        );
        break;
      }
      case SectionType.REGISTRANT: {
        this.updateField(
          adminPricing.adminPricedRegistrantFields,
          {
            fieldId: element.id,
            price: value,
            selectionId: selection,
            registrantId: element.registrantId,
          },
          ['fieldId', 'selectionId', 'registrantId']
        );
        break;
      }
      default: {
        // Handle regular fields
        this.updateField(
          adminPricing.adminPricedFields,
          {
            fieldId: element.id,
            price: value,
            selectionId: selection,
          },
          ['fieldId', 'selectionId']
        );
        break;
      }
    }
  }

  private updateField<T>(fieldArray: T[], newField: T, uniqueKeys: (keyof T)[]) {

    // Find the index of the existing field using the unique combination of keys
    const index = fieldArray.findIndex((field) =>
      uniqueKeys.every((key) => field[key] === newField[key])
    );

    if (index > -1) {
      // Replace the existing field
      fieldArray[index] = newField;
    } else {
      // Add a new field
      fieldArray.push(newField);
    }
  }

  getPriceChanges(
    summarySection: SummarySection,
    adminPriceOverrideRequest: AdminPriceOverrideRequest = {
      adminPricing: {
        adminPricedApplicantFields: [],
        adminPricedRegistrantFields: [],
        adminPricedOrganizationFields: [],
        adminPricedFields: [],
      },
    }
  ): AdminPriceOverrideRequest {
    summarySection.elements.forEach((element) => {
      if (element.templateOptions?.sections) {
        element.templateOptions.sections.forEach((section) => {
          // Process applicants
          section.applicants?.forEach((applicant) => {
            applicant.elements.forEach((applicantElement) => {
              this.processElement(
                applicantElement,
                adminPriceOverrideRequest.adminPricing.adminPricedApplicantFields,
                'applicantId',
                applicant.id
              );
            });
          });

          // Process selected registrants
          section.selectedRegistrants?.forEach((registrant) => {
            registrant.elements.forEach((registrantElement) => {
              this.processElement(
                registrantElement,
                adminPriceOverrideRequest.adminPricing.adminPricedRegistrantFields,
                'registrantId',
                registrant.id
              );
            });
          });

          // Process section elements
          section.elements.forEach((sectionElement) => {
            this.processElement(
              sectionElement,
              adminPriceOverrideRequest.adminPricing.adminPricedFields,
              null
            );
          });
        });
      }
    });

    return adminPriceOverrideRequest;
  }

  private processElement<T extends { fieldId: number; selectionId: number; price: number }>(
    element: SummarySectionElements,
    targetArray: (T & Partial<Record<'applicantId' | 'registrantId', number>>)[],
    idKey: 'applicantId' | 'registrantId' | null,
    idValue?: number
  ): void {
    if (!element.adminOverrideEligible || !element.selections) return;

    element.selections.forEach((selection) => {

      let price: number | undefined = undefined;

      if (selection.displayAdminOverridePrice && selection.adminOverridePrice !== undefined) {
        price = selection.adminOverridePrice;
      } else if (selection.displayDiscountCodePrice && selection.discountCodePrice !== undefined) {
        price = selection.discountCodePrice;
      }

      if (price !== undefined) {
        // Dynamically create the new field with optional idKey
        const newField: T & Partial<Record<'applicantId' | 'registrantId', number>> = {
          fieldId: element.id,
          price,
          selectionId: selection.id,
          ...(idKey && idValue !== undefined ? { [idKey]: idValue } : {}),
        } as T & Partial<Record<'applicantId' | 'registrantId', number>>;

        // Check if an entry already exists
        const existingIndex = targetArray.findIndex(
          (field) =>
            field.fieldId === newField.fieldId &&
            field.selectionId === newField.selectionId &&
            (!idKey || (field[idKey as keyof typeof field] === idValue))
        );

        if (existingIndex > -1) {
          targetArray[existingIndex] = newField;
        } else {
          targetArray.push(newField);
        }
      }
    });
  }

  adminOverride() {
    this.editing = true;
  }

  cancelOverride() {
    this.editing = false;
  }

  saveOverride() {

    this.loadingService.setLoading(true);

    this.formSaveService.save(this.props['uuId'], this.adminPriceOverrideRequest)
      .subscribe({
        next: () => {
          this.props['refreshCallback']();
        },
        error: () => {
          this.loadingService.setLoading(false);
        }
      })
  }

  applyDiscount(discountCode: SlInput) {

    this.loadingService.setLoading(true);

    this.formDiscountCodeService.addDiscountCode(this.props['uuId'], discountCode.value)
      .subscribe({
        next: () => {
          this.props['refreshCallback']();
        },
        error: (error: any) => {
          this.discountCodeResponse.errors = error.error.errors;
          this.loadingService.setLoading(false);
          this.changeDetectorRef.detectChanges();
        }
      })
  }

  clearDiscount() {

    this.loadingService.setLoading(true);

    this.formDiscountCodeService.removeDiscountCode(this.props['uuId'])
      .subscribe({
        next: () => {
          this.props['refreshCallback']();
        },
        error: (error: any) => {
          this.discountCodeResponse.errors = error.error.errors;
          this.loadingService.setLoading(false);
          this.changeDetectorRef.detectChanges();
        }
      })
  }
}
